import { createCompressedImageForApiData } from "./imageCompressionUtils";

/**
 * Creates a promise to load a single image with all its variants (original, compressed, cropped)
 * @param {Object} params - Parameters object
 * @param {Object} params.imgData - Image data from storage
 * @param {Object} params.info - Image info from shape_info or image_properties
 */
export const createImageLoadPromise = ({ imgData, info }) => {
  return new Promise((resolve) => {
    // CRITICAL: For images with hasBakedTransforms, we need to load the BAKED image
    // - hasBakedTransforms=true: compressedImageDataUrl or imageDataUrl contains BAKED pixels
    // - hasBakedTransforms=false: load imageDataUrl (current) or src (original)
    const imgDataUrl = imgData.imageDataUrl || info.imageDataUrl || "";
    const apiSrc = imgData.src || imgData.image_path || "";
    const hasBakedTransforms =
      imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false;
    const compressedImageDataUrl =
      imgData.compressedImageDataUrl || info.compressedImageDataUrl || "";

    // Check if URLs are stale blob URLs (which won't work after navigation)
    const isBlobUrl = imgDataUrl && imgDataUrl.startsWith("blob:");
    const isCompressedBlobUrl =
      compressedImageDataUrl && compressedImageDataUrl.startsWith("blob:");

    // Determine source to use
    let base64Src;
    if (hasBakedTransforms) {
      // For baked transforms, prioritize compressed baked image
      if (compressedImageDataUrl && !isCompressedBlobUrl) {
        base64Src = compressedImageDataUrl;
      } else if (imgDataUrl && !isBlobUrl) {
        base64Src = imgDataUrl;
      } else {
        base64Src = apiSrc;
        console.warn(
          `[MyCanvas] Baked image ${imgData.id} falling back to src`
        );
      }
    } else {
      // For non-baked, use imageDataUrl or src
      if (imgDataUrl && !isBlobUrl) {
        base64Src = imgDataUrl;
      } else if (isBlobUrl) {
        console.warn(`[MyCanvas] Skipping blob URL for image ${imgData.id}`);
        base64Src = apiSrc;
      } else {
        base64Src = apiSrc;
      }
    }

    if (!base64Src) {
      resolve({
        ...info,
        ...imgData,
        id: imgData.shape_id || imgData.id || imgData.image_id,
        x: imgData.x ?? info.x ?? 0,
        y: imgData.y ?? info.y ?? 0,
        width: imgData.width ?? info.width ?? 100,
        height: imgData.height ?? info.height ?? 100,
        src: "",
        image: null,
        maintainAspectRatio: imgData.maintainAspectRatio ?? true,
        compress: imgData.compress !== false ? true : false,
        originalImage: null,
        originalImageDataUrl:
          imgData.originalImageDataUrl || info.originalImageDataUrl || null,
        compressedImage: null,
        uncompressedCroppedImage: imgData.uncompressedCroppedImage || null,
      });
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = async () => {
      // For cropped images, we need to load the original image separately
      // if originalImageDataUrl exists and is different from the current src
      let originalImageDataUrl =
        imgData.originalImageDataUrl || info.originalImageDataUrl;

      // CRITICAL FIX: Check if originalImageDataUrl is a blob URL
      // For API images, blob URLs won't work after navigation
      if (originalImageDataUrl && originalImageDataUrl.startsWith("blob:")) {
        console.warn(
          `[MyCanvas] originalImageDataUrl is a blob URL for image ${imgData.id}, cannot load original image after navigation`
        );
        originalImageDataUrl = null;
      }

      // Support both property names for uncompressed cropped image URL
      let uncompressedCroppedImageDataUrl =
        imgData.uncompressedCroppedImageDataUrl ||
        info.uncompressedCroppedImageDataUrl ||
        imgData.uncompressedCroppedImageUrl ||
        info.uncompressedCroppedImageUrl;

      // CRITICAL FIX: Check if uncompressedCroppedImageDataUrl is a blob URL
      if (
        uncompressedCroppedImageDataUrl &&
        uncompressedCroppedImageDataUrl.startsWith("blob:")
      ) {
        console.warn(
          `[MyCanvas] uncompressedCroppedImageDataUrl is a blob URL for image ${imgData.id}, setting to null`
        );
        uncompressedCroppedImageDataUrl = null;
      }

      const hasCropData = imgData.cropData || info.cropData;
      const hasBakedTransforms =
        imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false;

      // Check if this is a cropped image that needs original image loaded
      // For images with baked transforms (flips/straightening), we might not have originalImageDataUrl
      // because the transforms are already in the cropped image URL
      const needsOriginalImage =
        hasCropData &&
        originalImageDataUrl &&
        originalImageDataUrl !== base64Src &&
        !hasBakedTransforms;

      // Also handle images with baked transforms but need original for uncrop
      const hasBakedTransformsWithOriginal =
        hasCropData &&
        hasBakedTransforms &&
        originalImageDataUrl &&
        originalImageDataUrl !== base64Src;

      if (needsOriginalImage || hasBakedTransformsWithOriginal) {
        // Load the original image for uncropping functionality
        const originalImage = new Image();
        originalImage.crossOrigin = "anonymous";

        // Load uncompressed cropped version if available
        const loadUncompressedCropped = new Promise((resolveUncropped) => {
          if (
            uncompressedCroppedImageDataUrl &&
            uncompressedCroppedImageDataUrl !== base64Src
          ) {
            const uncompressedCroppedImage = new Image();
            uncompressedCroppedImage.crossOrigin = "anonymous";
            uncompressedCroppedImage.onload = () =>
              resolveUncropped(uncompressedCroppedImage);
            uncompressedCroppedImage.onerror = () => resolveUncropped(image); // Fallback to cropped image
            uncompressedCroppedImage.src = uncompressedCroppedImageDataUrl;
          } else {
            resolveUncropped(image); // No uncompressed version, use current
          }
        });

        originalImage.onload = async () => {
          const uncompressedCroppedImage = await loadUncompressedCropped;

          resolve({
            ...info,
            ...imgData,
            id: imgData.shape_id || imgData.id || imgData.image_id,
            x: imgData.x ?? info.x ?? 0,
            y: imgData.y ?? info.y ?? 0,
            width: imgData.width ?? info.width ?? 100,
            height: imgData.height ?? info.height ?? 100,
            src: base64Src,
            image,
            maintainAspectRatio: imgData.maintainAspectRatio ?? true,
            compress: imgData.compress !== false ? true : false,
            originalImage: originalImage,
            originalImageDataUrl: originalImageDataUrl,
            compressedImage: imgData.compressedImage || null,
            uncompressedCroppedImage: uncompressedCroppedImage,
            cropData: imgData.cropData || info.cropData,
            flipHorizontal:
              imgData.flipHorizontal ?? info.flipHorizontal ?? false,
            flipVertical: imgData.flipVertical ?? info.flipVertical ?? false,
            originalCropRotation:
              imgData.originalCropRotation ?? info.originalCropRotation ?? 0,
            hasBakedTransforms:
              imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false,
            rotation: imgData.rotation ?? info.rotation ?? 0,
            scaleX: 1,
            scaleY: 1,
            originalWidth:
              imgData.originalWidth ??
              info.originalWidth ??
              imgData.width ??
              info.width ??
              100,
            originalHeight:
              imgData.originalHeight ??
              info.originalHeight ??
              imgData.height ??
              info.height ??
              100,
          });
        };
        originalImage.onerror = async () => {
          const uncompressedCroppedImage = await loadUncompressedCropped;

          resolve({
            ...info,
            ...imgData,
            id: imgData.shape_id || imgData.id || imgData.image_id,
            x: imgData.x ?? info.x ?? 0,
            y: imgData.y ?? info.y ?? 0,
            width: imgData.width ?? info.width ?? 100,
            height: imgData.height ?? info.height ?? 100,
            src: base64Src,
            image,
            maintainAspectRatio: imgData.maintainAspectRatio ?? true,
            compress: imgData.compress !== false ? true : false,
            originalImage: image,
            originalImageDataUrl: originalImageDataUrl,
            compressedImage: imgData.compressedImage || null,
            uncompressedCroppedImage: uncompressedCroppedImage,
            cropData: imgData.cropData || info.cropData,
            flipHorizontal:
              imgData.flipHorizontal ?? info.flipHorizontal ?? false,
            flipVertical: imgData.flipVertical ?? info.flipVertical ?? false,
            originalCropRotation:
              imgData.originalCropRotation ?? info.originalCropRotation ?? 0,
            hasBakedTransforms:
              imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false,
            rotation: imgData.rotation ?? info.rotation ?? 0,
            scaleX: 1,
            scaleY: 1,
            originalWidth:
              imgData.originalWidth ??
              info.originalWidth ??
              imgData.width ??
              info.width ??
              100,
            originalHeight:
              imgData.originalHeight ??
              info.originalHeight ??
              imgData.height ??
              info.height ??
              100,
          });
        };
        originalImage.src = originalImageDataUrl;
      } else {
        // Not a cropped image, OR cropped image with baked transforms but no original URL
        // For images with baked transforms (straightened), the image URL already contains the transforms
        const compressionResult = await createCompressedImageForApiData(
          image,
          imgData
        );

        resolve({
          ...info,
          ...imgData,
          id: imgData.shape_id || imgData.id || imgData.image_id,
          x: imgData.x ?? info.x ?? 0,
          y: imgData.y ?? info.y ?? 0,
          width: imgData.width ?? info.width ?? 100,
          height: imgData.height ?? info.height ?? 100,
          src: base64Src,
          image: compressionResult.finalImage,
          maintainAspectRatio: imgData.maintainAspectRatio ?? true,
          compress: imgData.compress !== false ? true : false,
          originalImage: image,
          compressedImage: compressionResult.compressedImageObj,
          uncompressedCroppedImage: compressionResult.uncompressedImage,
          cropData: imgData.cropData || info.cropData,
          originalCropRotation:
            imgData.originalCropRotation ?? info.originalCropRotation ?? 0,
          flipHorizontal:
            imgData.flipHorizontal ?? info.flipHorizontal ?? false,
          flipVertical: imgData.flipVertical ?? info.flipVertical ?? false,
          hasBakedTransforms:
            imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false,
          rotation: imgData.rotation ?? info.rotation ?? 0,
          scaleX:
            imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
              ? Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
              : imgData.flipHorizontal ?? info.flipHorizontal ?? false
              ? -Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
              : imgData.scaleX ?? info.scaleX ?? 1,
          scaleY:
            imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
              ? Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
              : imgData.flipVertical ?? info.flipVertical ?? false
              ? -Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
              : imgData.scaleY ?? info.scaleY ?? 1,
          originalWidth:
            imgData.originalWidth ??
            info.originalWidth ??
            imgData.width ??
            info.width ??
            100,
          originalHeight:
            imgData.originalHeight ??
            info.originalHeight ??
            imgData.height ??
            info.height ??
            100,
          originalAspectRatio:
            imgData.originalAspectRatio ??
            info.originalAspectRatio ??
            (imgData.width ?? info.width ?? 100) /
              (imgData.height ?? info.height ?? 100),
        });
      }
    };

    image.onerror = (error) => {
      resolve({
        ...info,
        ...imgData,
        id: imgData.shape_id || imgData.id || imgData.image_id,
        x: imgData.x ?? info.x ?? 0,
        y: imgData.y ?? info.y ?? 0,
        width: imgData.width ?? info.width ?? 100,
        height: imgData.height ?? info.height ?? 100,
        src: base64Src,
        image: null,
        maintainAspectRatio: imgData.maintainAspectRatio ?? true,
        originalImage: null,
        compressedImage: null,
        flipHorizontal: imgData.flipHorizontal ?? info.flipHorizontal ?? false,
        flipVertical: imgData.flipVertical ?? info.flipVertical ?? false,
        hasBakedTransforms:
          imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false,
        rotation: imgData.rotation ?? info.rotation ?? 0,
        scaleX:
          imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
            ? Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
            : imgData.flipHorizontal ?? info.flipHorizontal ?? false
            ? -Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
            : imgData.scaleX ?? info.scaleX ?? 1,
        scaleY:
          imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
            ? Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
            : imgData.flipVertical ?? info.flipVertical ?? false
            ? -Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
            : imgData.scaleY ?? info.scaleY ?? 1,
      });
    };

    // Add timeout for image loading
    const timeout = setTimeout(() => {
      image.onload = null;
      image.onerror = null;
      resolve({
        ...info,
        ...imgData,
        id: imgData.shape_id || imgData.id || imgData.image_id,
        x: imgData.x ?? info.x ?? 0,
        y: imgData.y ?? info.y ?? 0,
        width: imgData.width ?? info.width ?? 100,
        height: imgData.height ?? info.height ?? 100,
        src: base64Src,
        image: null,
        maintainAspectRatio: imgData.maintainAspectRatio ?? true,
        compress: imgData.compress !== false ? true : false,
        originalImage: null,
        originalImageDataUrl:
          imgData.originalImageDataUrl || info.originalImageDataUrl || null,
        compressedImage: null,
      });
    }, 10000); // 10 second timeout

    // Clear timeout and handle additional image load logic if needed
    image.onload = async () => {
      clearTimeout(timeout);

      // For cropped images, we need to load the original image separately
      // if originalImageDataUrl exists and is different from the current src
      let originalImageDataUrl =
        imgData.originalImageDataUrl || info.originalImageDataUrl;

      // CRITICAL FIX: Check if originalImageDataUrl is a blob URL (won't work after navigation)
      if (originalImageDataUrl && originalImageDataUrl.startsWith("blob:")) {
        console.warn(
          `[MyCanvas] originalImageDataUrl is a blob URL for image ${imgData.id}, cannot load original image after navigation`
        );
        originalImageDataUrl = null;
      }

      const isCroppedImage =
        originalImageDataUrl && originalImageDataUrl !== base64Src;

      if (isCroppedImage) {
        // Load the original image for uncropping functionality
        // Use loaded cropped image's natural dimensions as fallback so redo displays correct size when history missed width/height
        const fallbackW = (image && image.naturalWidth > 0) ? image.naturalWidth : (imgData.width ?? info.width ?? 100);
        const fallbackH = (image && image.naturalHeight > 0) ? image.naturalHeight : (imgData.height ?? info.height ?? 100);
        const originalImage = new Image();
        originalImage.crossOrigin = "anonymous";
        originalImage.onload = () => {
          resolve({
            ...info,
            ...imgData,
            id: imgData.shape_id || imgData.id || imgData.image_id,
            x: imgData.x ?? info.x ?? 0,
            y: imgData.y ?? info.y ?? 0,
            width: imgData.width ?? info.width ?? fallbackW,
            height: imgData.height ?? info.height ?? fallbackH,
            src: base64Src,
            image,
            maintainAspectRatio: imgData.maintainAspectRatio ?? true,
            compress: imgData.compress !== false ? true : false,
            originalImage: originalImage,
            originalImageDataUrl: originalImageDataUrl,
            compressedImage: imgData.compressedImage || null,
            uncompressedCroppedImage: imgData.uncompressedCroppedImage || null,
            cropData: imgData.cropData || info.cropData,
            originalWidth:
              imgData.originalWidth ??
              info.originalWidth ??
              imgData.width ??
              info.width ??
              fallbackW,
            originalHeight:
              imgData.originalHeight ??
              info.originalHeight ??
              imgData.height ??
              info.height ??
              fallbackH,
          });
        };
        originalImage.onerror = () => {
          resolve({
            ...info,
            ...imgData,
            id: imgData.shape_id || imgData.id || imgData.image_id,
            x: imgData.x ?? info.x ?? 0,
            y: imgData.y ?? info.y ?? 0,
            width: imgData.width ?? info.width ?? fallbackW,
            height: imgData.height ?? info.height ?? fallbackH,
            src: base64Src,
            image,
            maintainAspectRatio: imgData.maintainAspectRatio ?? true,
            compress: imgData.compress !== false ? true : false,
            originalImage: image,
            originalImageDataUrl: originalImageDataUrl,
            compressedImage: imgData.compressedImage || null,
            uncompressedCroppedImage: imgData.uncompressedCroppedImage || null,
            cropData: imgData.cropData || info.cropData,
            originalWidth:
              imgData.originalWidth ??
              info.originalWidth ??
              imgData.width ??
              info.width ??
              fallbackW,
            originalHeight:
              imgData.originalHeight ??
              info.originalHeight ??
              imgData.height ??
              info.height ??
              fallbackH,
          });
        };
        originalImage.src = originalImageDataUrl;
      } else {
        // Not a cropped image, use the same image for both
        // But if we have crop data, we need to handle this differently
        const finalCropData = imgData.cropData || info.cropData;

        resolve({
          ...info,
          ...imgData,
          id: imgData.shape_id || imgData.id || imgData.image_id,
          x: imgData.x ?? info.x ?? 0,
          y: imgData.y ?? info.y ?? 0,
          width: imgData.width ?? info.width ?? 100,
          height: imgData.height ?? info.height ?? 100,
          src: base64Src,
          image,
          maintainAspectRatio: imgData.maintainAspectRatio ?? true,
          originalImage: image,
          originalImageDataUrl: originalImageDataUrl || null,
          compressedImage: null,
          cropData: finalCropData,
          originalCropRotation:
            imgData.originalCropRotation ?? info.originalCropRotation ?? 0,
          flipHorizontal:
            imgData.flipHorizontal ?? info.flipHorizontal ?? false,
          flipVertical: imgData.flipVertical ?? info.flipVertical ?? false,
          hasBakedTransforms:
            imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false,
          rotation: imgData.rotation ?? info.rotation ?? 0,
          scaleX:
            imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
              ? Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
              : imgData.flipHorizontal ?? info.flipHorizontal ?? false
              ? -Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
              : imgData.scaleX ?? info.scaleX ?? 1,
          scaleY:
            imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
              ? Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
              : imgData.flipVertical ?? info.flipVertical ?? false
              ? -Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
              : imgData.scaleY ?? info.scaleY ?? 1,
        });
      }
    };

    image.onerror = (error) => {
      clearTimeout(timeout);

      resolve({
        ...info,
        ...imgData,
        id: imgData.shape_id || imgData.id || imgData.image_id,
        x: imgData.x ?? info.x ?? 0,
        y: imgData.y ?? info.y ?? 0,
        width: imgData.width ?? info.width ?? 100,
        height: imgData.height ?? info.height ?? 100,
        src: base64Src,
        image: null,
        maintainAspectRatio: imgData.maintainAspectRatio ?? true,
        originalImage: null,
        flipHorizontal: imgData.flipHorizontal ?? info.flipHorizontal ?? false,
        flipVertical: imgData.flipVertical ?? info.flipVertical ?? false,
        hasBakedTransforms:
          imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false,
        rotation: imgData.rotation ?? info.rotation ?? 0,
        scaleX:
          imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
            ? Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
            : imgData.flipHorizontal ?? info.flipHorizontal ?? false
            ? -Math.abs(imgData.scaleX ?? info.scaleX ?? 1)
            : imgData.scaleX ?? info.scaleX ?? 1,
        scaleY:
          imgData.hasBakedTransforms ?? info.hasBakedTransforms ?? false
            ? Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
            : imgData.flipVertical ?? info.flipVertical ?? false
            ? -Math.abs(imgData.scaleY ?? info.scaleY ?? 1)
            : imgData.scaleY ?? info.scaleY ?? 1,
        originalImageDataUrl:
          imgData.originalImageDataUrl || info.originalImageDataUrl || null,
        compressedImage: imgData.compressedImage || null,
        uncompressedCroppedImage: imgData.uncompressedCroppedImage || null,
      });
    };

    image.src = base64Src;
  });
};
